import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Cookie, Analytics, Settings, Lock } from '@mui/icons-material';

const Cookies = () => {
    const cookieTypes = [
        {
            name: 'Cookies essentiels',
            icon: Lock,
            color: 'bg-green-100 text-green-700',
            required: true,
            description: 'Ces cookies sont absolument nécessaires au fonctionnement du site. Ils garantissent les fonctionnalités de base et les caractéristiques de sécurité du site.',
            examples: [
                'Cookies de session pour maintenir votre connexion',
                'Cookies de sécurité pour la protection CSRF',
                'Cookies de préférences de base (langue, région)'
            ],
            duration: 'Session'
        },
        {
            name: 'Cookies analytiques',
            icon: Analytics,
            color: 'bg-blue-100 text-blue-700',
            required: false,
            description: 'Ces cookies nous aident à comprendre comment les visiteurs interagissent avec notre site en collectant et rapportant des informations de manière anonyme.',
            examples: [
                'Mesure du trafic et du comportement des utilisateurs',
                'Analyse des pages les plus visitées',
                'Identification des problèmes techniques'
            ],
            duration: '2 ans'
        },
        {
            name: 'Cookies de préférences',
            icon: Settings,
            color: 'bg-purple-100 text-purple-700',
            required: false,
            description: 'Ces cookies permettent au site de se souvenir de vos choix et d\'offrir des fonctionnalités améliorées et plus personnelles.',
            examples: [
                'Préférences de langue et de région',
                'Paramètres d\'affichage',
                'Thème (clair/sombre) si applicable'
            ],
            duration: '1 an'
        }
    ];

    return (
        <>
            <Helmet>
                <title>Politique des Cookies - Netikash Developer Portal</title>
                <meta name="description" content="Politique des cookies de Netikash : types de cookies utilisés, finalités, durée de conservation et gestion de vos préférences." />
                <meta property="og:title" content="Politique des Cookies - Netikash" />
                <meta property="og:description" content="Découvrez comment Netikash utilise les cookies et comment gérer vos préférences." />
                <link rel="canonical" href="https://dev.netikash.com/cookies" />
            </Helmet>
            <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 pt-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    {/* Header */}
                    <div className="mb-12 text-center">
                        <div className="inline-flex items-center px-4 py-2 rounded-full bg-amber-100 text-amber-800 mb-4">
                            <Cookie className="mr-2" fontSize="small" />
                            <span className="text-sm font-medium">Gestion des Cookies</span>
                        </div>
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">Politique des Cookies</h1>
                        <p className="text-xl text-gray-600">
                            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                    </div>

                    {/* Introduction */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
                        <p className="text-gray-700 leading-relaxed mb-4">
                            Cette politique explique comment Netikash utilise les cookies et technologies similaires sur notre portail développeur. Elle décrit les types de cookies que nous utilisons, les informations que nous collectons, comment nous les utilisons et comment vous pouvez contrôler vos préférences.
                        </p>
                        <p className="text-gray-700 leading-relaxed">
                            En utilisant notre site, vous acceptez l'utilisation de cookies conformément à cette politique. Vous pouvez modifier vos préférences à tout moment via les paramètres de votre navigateur ou notre bannière de cookies.
                        </p>
                    </div>

                    {/* Qu'est-ce qu'un cookie */}
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Qu'est-ce qu'un cookie ?</h2>
                        <div className="space-y-4 text-gray-700">
                            <p>
                                Un cookie est un petit fichier texte stocké sur votre appareil (ordinateur, tablette, smartphone) lorsque vous visitez un site web. Les cookies permettent au site de mémoriser vos actions et préférences sur une période donnée, évitant ainsi de devoir les ressaisir à chaque visite.
                            </p>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <p className="text-blue-900 text-sm">
                                    <strong>Important :</strong> Les cookies ne peuvent pas endommager votre appareil ni contenir de virus. Ils sont uniquement utilisés pour améliorer votre expérience de navigation.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Types de cookies */}
                    <section className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Types de cookies utilisés</h2>
                        <div className="space-y-6">
                            {cookieTypes.map((type, index) => {
                                const Icon = type.icon;
                                return (
                                    <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                                        <div className="flex items-start space-x-4 mb-4">
                                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${type.color}`}>
                                                <Icon />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h3 className="text-xl font-bold text-gray-900">{type.name}</h3>
                                                    {type.required ? (
                                                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                                                            Obligatoire
                                                        </span>
                                                    ) : (
                                                        <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">
                                                            Optionnel
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-gray-700 mb-4">{type.description}</p>
                                                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">Exemples :</h4>
                                                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                                                        {type.examples.map((example, idx) => (
                                                            <li key={idx}>{example}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                <div className="flex items-center space-x-4 text-sm text-gray-600">
                                                    <span>
                                                        <strong>Durée :</strong> {type.duration}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* Finalités */}
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Finalités d'utilisation</h2>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="border border-gray-200 rounded-lg p-4">
                                <h3 className="font-semibold text-gray-900 mb-2">Fonctionnalités essentielles</h3>
                                <p className="text-sm text-gray-700">Authentification, sécurité, préférences de base</p>
                            </div>
                            <div className="border border-gray-200 rounded-lg p-4">
                                <h3 className="font-semibold text-gray-900 mb-2">Analyse et amélioration</h3>
                                <p className="text-sm text-gray-700">Compréhension de l'utilisation, optimisation</p>
                            </div>
                            <div className="border border-gray-200 rounded-lg p-4">
                                <h3 className="font-semibold text-gray-900 mb-2">Personnalisation</h3>
                                <p className="text-sm text-gray-700">Mémorisation de vos préférences</p>
                            </div>
                            <div className="border border-gray-200 rounded-lg p-4">
                                <h3 className="font-semibold text-gray-900 mb-2">Performance</h3>
                                <p className="text-sm text-gray-700">Amélioration de la vitesse et de l'efficacité</p>
                            </div>
                        </div>
                    </section>

                    {/* Gestion des cookies */}
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Gestion de vos préférences</h2>
                        <div className="space-y-4 text-gray-700">
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-2">Via notre bannière de cookies</h3>
                                <p className="mb-2">
                                    Lors de votre première visite, une bannière de cookies s'affiche vous permettant de :
                                </p>
                                <ul className="list-disc list-inside space-y-1 ml-4">
                                    <li>Accepter tous les cookies</li>
                                    <li>Refuser les cookies non essentiels</li>
                                    <li>Personnaliser vos préférences par catégorie</li>
                                </ul>
                                <p className="mt-4 text-sm text-gray-600">
                                    Vous pouvez modifier vos préférences à tout moment en cliquant sur le lien "Gérer les cookies" en bas de la page ou en supprimant les cookies de votre navigateur.
                                </p>
                            </div>
                            <div className="border-t border-gray-200 pt-4 mt-4">
                                <h3 className="font-semibold text-gray-900 mb-2">Via les paramètres de votre navigateur</h3>
                                <p className="mb-4">La plupart des navigateurs vous permettent de contrôler les cookies via leurs paramètres :</p>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="font-semibold text-gray-900 mb-2 text-sm">Chrome</h4>
                                        <p className="text-sm text-gray-700">Paramètres → Confidentialité et sécurité → Cookies</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="font-semibold text-gray-900 mb-2 text-sm">Firefox</h4>
                                        <p className="text-sm text-gray-700">Options → Vie privée et sécurité → Cookies</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="font-semibold text-gray-900 mb-2 text-sm">Safari</h4>
                                        <p className="text-sm text-gray-700">Préférences → Confidentialité → Cookies</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="font-semibold text-gray-900 mb-2 text-sm">Edge</h4>
                                        <p className="text-sm text-gray-700">Paramètres → Cookies et autorisations de site</p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                                <p className="text-yellow-900 text-sm">
                                    <strong>Note :</strong> La désactivation de certains cookies peut affecter le fonctionnement du site et limiter certaines fonctionnalités.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Cookies tiers */}
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Cookies tiers</h2>
                        <div className="space-y-4 text-gray-700">
                            <p>
                                Nous pouvons utiliser des services tiers qui déposent également des cookies sur votre appareil. Ces cookies sont régis par les politiques de confidentialité respectives de ces tiers :
                            </p>
                            <div className="space-y-3">
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <h3 className="font-semibold text-gray-900 mb-1">Services d'analyse</h3>
                                    <p className="text-sm text-gray-700">
                                        Nous utilisons des services d'analyse pour comprendre l'utilisation de notre site. Ces services peuvent déposer leurs propres cookies.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Durée de conservation */}
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Durée de conservation</h2>
                        <div className="space-y-4 text-gray-700">
                            <p>Les cookies sont conservés pour différentes durées selon leur type :</p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li><strong>Cookies de session</strong> : Supprimés à la fermeture de votre navigateur</li>
                                <li><strong>Cookies persistants</strong> : Conservés jusqu'à leur expiration ou jusqu'à ce que vous les supprimiez</li>
                                <li><strong>Cookies analytiques</strong> : Généralement conservés pendant 2 ans maximum</li>
                                <li><strong>Cookies de préférences</strong> : Généralement conservés pendant 1 an</li>
                            </ul>
                        </div>
                    </section>

                    {/* Mises à jour */}
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Mises à jour de cette politique</h2>
                        <p className="text-gray-700">
                            Nous pouvons mettre à jour cette politique des cookies de temps à autre pour refléter les changements dans notre utilisation des cookies ou pour d'autres raisons opérationnelles, légales ou réglementaires. Nous vous encourageons à consulter régulièrement cette page.
                        </p>
                    </section>

                    {/* Contact */}
                    <section className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl shadow-sm border border-amber-200 p-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions sur les cookies ?</h2>
                        <p className="text-gray-700 mb-4">
                            Si vous avez des questions concernant notre utilisation des cookies, contactez-nous :
                        </p>
                        <div className="bg-white rounded-lg p-4 border border-amber-200">
                            <p className="font-semibold text-gray-900 mb-2">Netikash</p>
                            <p className="text-sm mb-1">
                                <strong>Email :</strong> <a href="mailto:privacy@netikash.com" className="text-amber-600 hover:underline">privacy@netikash.com</a>
                            </p>
                            <p className="text-sm">
                                <strong>Adresse :</strong> 1 Mpolo coin Kasangulu, Lubumbashi, RD Congo
                            </p>
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

export default Cookies;